import React, { PropsWithChildren, useState } from 'react';
import { Trans } from 'react-i18next';
import { Strong } from '../../../../components/atoms/Strong/Strong';
import { Namespace } from '../../../../translation/namespace';
import { Icons, SVGIcon } from '../../../../components/atoms/SVG';
import Btn, { BtnModifier } from '../../../../components/molecules/Btn';
import { TooltipPlacement } from '../../../../components/atoms/Tooltip';
import ModalDialog from '../../../../components/organisms/ModalDialog/ModalDialog';
import YesNoForm from '../../../../components/forms/DeleteForm/YesNoForm';
import {
  AnnouncementCreateOrUpdate,
  AnnouncementCreateOrUpdateForm,
} from '../../forms/AnnouncementCreateOrUpdate/AnnouncementCreateOrUpdate';
import { MandatoryAnnouncementsAnalysis } from '../MandatoryAnnouncementsAnalysis/MandatoryAnnouncementsAnalysis';
import IForm from '../../../../types/IForm';
import { useSonTranslation } from '../../../../translation/useSonTranslation';
import { asFetchableView } from '../../../../_lib/ViewFetcher';
import { FetchType } from '../../../../types/api/FetchType';

const MandatoryAnnouncementsAnalysisView = asFetchableView(MandatoryAnnouncementsAnalysis);

type INTERACTION = 'edit' | 'delete' | 'analysis';

type AnnouncementInteractions = {
  title: string;
  deleteForm?: IForm;
  editForm?: AnnouncementCreateOrUpdateForm;
  analysis?: { fetchType: FetchType; fetchUrl: string };
};

export function AnnouncementInteractions({
  title,
  deleteForm,
  editForm,
  analysis,
  children,
}: PropsWithChildren<AnnouncementInteractions>) {
  const { t } = useSonTranslation('Announcement');

  const [activeInteraction, setActiveInteraction] = useState<INTERACTION | null>(null);

  const closeModal = () => setActiveInteraction(null);

  /**
   * Avoid retuning an empty <div>
   */
  if (!deleteForm && !editForm && !analysis) {
    return null;
  }

  const createButton = (interactionType: INTERACTION, sprite: SVGIcon) => (
    <div className="announcement__button-container">
      <Btn
        modifier={[BtnModifier.MINOR_ACTION, BtnModifier.TRANSPARENT]}
        text={t(`interactions.${interactionType}.title`)}
        titleText={t(`interactions.${interactionType}.title`)}
        hiddenText
        svgSpriteId={sprite}
        tooltipPlacement={TooltipPlacement.TOP}
        onClick={() => setActiveInteraction(interactionType)}
      />
    </div>
  );

  const getDeleteQuestion = () => (
    <Trans i18nKey={`${Namespace.Announcement}:interactions.delete.question`}>
      <Strong>{title}</Strong>
    </Trans>
  );

  return (
    <div className="announcement__interactions">
      {children}
      {analysis && createButton('analysis', Icons.ANALYSIS)}
      {editForm && createButton('edit', Icons.EDIT_PENCIL)}
      {deleteForm && createButton('delete', Icons.DELETE_TRASHCAN)}
      {activeInteraction !== null && (
        <ModalDialog
          closeModal={closeModal}
          headline={t(`interactions.${activeInteraction}.title`)}
          initialFocusIndex={1}
        >
          {activeInteraction === 'analysis' && analysis && (
            // @ts-expect-error href not available/required here, not a real view.
            <MandatoryAnnouncementsAnalysisView fetchUrl={analysis.fetchUrl} fetchType={FetchType.lazy} />
          )}
          {activeInteraction === 'delete' && deleteForm && (
            <YesNoForm
              form={deleteForm}
              id="announcement-delete-form"
              validateResponseStatus={statusNumber => statusNumber === 200}
              transNamespaceAndKey={`${Namespace.Announcement}:interactions.delete`}
              headline={getDeleteQuestion()}
              cancelAction={closeModal}
            />
          )}
          {activeInteraction === 'edit' && editForm && <AnnouncementCreateOrUpdate form={editForm} type="update" />}
        </ModalDialog>
      )}
    </div>
  );
}
